const API_BASE_URL = "https://stage.proctorview.com/api/v1";
// const API_BASE_URL = "http://localhost:3001/api/v1";

const DEFAULT_RETRY_CONFIG = {
  maxRetries: 10,
  retryDelay: 1000,
  retryOn: [408, 429, 500, 502, 503, 504],
};

class ApiClient {
  constructor(baseURL, retryConfig = DEFAULT_RETRY_CONFIG) {
    this.baseURL = baseURL;
    this.retryConfig = { ...DEFAULT_RETRY_CONFIG, ...retryConfig };
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    const defaultHeaders = {};

    if (!(options.body instanceof FormData)) {
      defaultHeaders["Content-Type"] = "application/json";
    }

    let token = null;
    if (typeof window !== "undefined") {
      token = localStorage.getItem("api_key");
    }

    if (token) {
      defaultHeaders["api_key"] = token;
    }

    const config = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...(options.headers || {}),
      },
    };

    let lastError;
    const { maxRetries, retryDelay, retryOn } = this.retryConfig;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, config);

        if (response.status === 401 || response.status === 403) {
          if (typeof window !== "undefined") {
            localStorage.removeItem("api_key");
          }

          const error = new Error("Authentication failed");
          error.status = response.status;
          throw error;
        }

        if (!response.ok) {
          if (retryOn.includes(response.status) && attempt < maxRetries) {
            await this.sleep(retryDelay * (attempt + 1));
            continue;
          }
          const error = new Error(`HTTP error: ${response.status}`);
          error.status = response.status;
          throw error;
        }

        const text = await response.text();
        return text ? JSON.parse(text) : null;
      } catch (error) {
        lastError = error;

        if (attempt < maxRetries && this.isRetryableError(error)) {
          console.log(
            `[ApiClient] Retry ${attempt + 1}/${maxRetries} after ${retryDelay * (attempt + 1)}ms`,
          );
          await this.sleep(retryDelay * (attempt + 1));
        } else {
          throw error;
        }
      }
    }

    console.error("API request failed after retries:", lastError);
    throw lastError;
  }

  isRetryableError(error) {
    if (error.name === "TypeError" && error.message.includes("fetch")) {
      return true;
    }
    return false;
  }

  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  get(endpoint) {
    return this.request(endpoint, { method: "GET" });
  }

  post(endpoint, data) {
    return this.request(endpoint, {
      method: "POST",
      body:
        data instanceof FormData
          ? data
          : data
            ? JSON.stringify(data)
            : undefined,
    });
  }

  put(endpoint, data) {
    return this.request(endpoint, {
      method: "PUT",
      body:
        data instanceof FormData
          ? data
          : data
            ? JSON.stringify(data)
            : undefined,
    });
  }

  delete(endpoint) {
    return this.request(endpoint, { method: "DELETE" });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
